import create from 'zustand'
import { combine } from 'zustand/middleware'
import { useSelectionStore } from '@editorStores'
import { tabConfig as windAssignmentTagConfig } from '../components/WindloadsTab/components/WindLoadsForm/constants'
import { tabConfig } from '../constants'

type LoadsTab = keyof typeof tabConfig
type WindAssignmentTab = keyof typeof windAssignmentTagConfig
type GlobalLoadsMode = 'comments'

interface Params {
  activeRoofAssignmentTab?: WindAngle | null
  tab?: LoadsTab | null
  windAssignmentTab?: WindAssignmentTab | null
  mode?: GlobalLoadsMode | null
}

interface GlobalLoadsViewState {
  activeWindAngle: WindAngle | null
  tab: LoadsTab | null
  windAssignmentTab: WindAssignmentTab | null
}

const initialViewState: GlobalLoadsViewState = {
  activeWindAngle: null,
  tab: null,
  windAssignmentTab: null,
}

/** View state of the GlobalLoads page (formerly query params; session-only by design). */
const useGlobalLoadsViewStore = create(
  combine({ ...initialViewState }, set => ({
    setView: (partial: Partial<GlobalLoadsViewState>) => set(state => ({ ...state, ...partial })),
    /** Former `setQueryReset` semantics: everything not passed resets. */
    resetView: (partial: Partial<GlobalLoadsViewState> = {}) =>
      set(state => ({ ...state, ...initialViewState, ...partial })),
  })),
)

/**
 * Selection/tab state of the GlobalLoads page. Historically the last mode still living in query
 * params (hence the name, kept to avoid churning imports); the mode now lives in the global
 * selection store and the tab/angle state in a page-local view store. The public API is
 * unchanged.
 */
const useGlobalLoadsQueryParams = () => {
  const activeWindAngle = useGlobalLoadsViewStore(state => state.activeWindAngle)
  const tab = useGlobalLoadsViewStore(state => state.tab)
  const windAssignmentTab = useGlobalLoadsViewStore(state => state.windAssignmentTab)
  const setView = useGlobalLoadsViewStore(state => state.setView)
  const resetView = useGlobalLoadsViewStore(state => state.resetView)

  const storeMode = useSelectionStore(state => state.mode)
  const switchMode = useSelectionStore(state => state.switchMode)

  const modeTyped: GlobalLoadsMode | null = storeMode === 'comments' ? 'comments' : null

  const reset = () => {
    resetView()
    if (modeTyped) switchMode('neutral')
  }

  const setActiveWindAngle = (newActiveWindAngle?: WindAngle | null) => {
    setView({ activeWindAngle: newActiveWindAngle ?? null })
  }

  const toggleTab = (newTab?: LoadsTab) => {
    resetView({ tab: newTab ?? null })
    if (modeTyped) switchMode('neutral')
  }

  const toggleWindAssignmentTab = (newWindAssignmentTab?: WindAssignmentTab) => {
    resetView({ windAssignmentTab: newWindAssignmentTab ?? null, tab })
    if (modeTyped) switchMode('neutral')
  }

  // Toggle an exclusive page mode (e.g. comments). Keeps the current loads tab
  // so leaving comments mode returns to the same tab.
  const toggleMode = (newMode?: GlobalLoadsMode) => {
    resetView({ tab })
    switchMode(!newMode || modeTyped === newMode ? 'neutral' : newMode)
  }

  return {
    params: {
      activeRoofAssignmentTab: activeWindAngle,
      tab,
      windAssignmentTab: windAssignmentTab || 'walls',
      mode: modeTyped,
    } as Params,
    modes: {
      isCommentsMode: modeTyped === 'comments',
    },
    actions: {
      reset,
      setActiveWindAngle,
      toggleTab,
      toggleWindAssignmentTab,
      toggleMode,
    },
  }
}

export default useGlobalLoadsQueryParams
